import { useNavigate } from "react-router-dom";

const sharedStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0e1a;
    --surface: #111827;
    --surface2: #1a2235;
    --border: rgba(255,255,255,0.07);
    --accent: #00d4ff;
    --accent2: #7b5ea7;
    --success: #22c55e;
    --text: #e8edf5;
    --muted: #6b7a99;
    --font-display: 'Syne', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-display);
    min-height: 100vh;
  }

  .page {
    min-height: 100vh;
    background: radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.04) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(123,94,167,0.06) 0%, transparent 50%),
                var(--bg);
  }

  .nav {
    padding: 20px 48px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(10,14,26,0.85);
    backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: 0.02em;
    cursor: pointer;
  }

  .nav-logo span { font-size: 26px; }

  .nav-actions { display: flex; align-items: center; gap: 12px; }

  .btn-ghost {
    background: transparent;
    color: var(--muted);
    border: 1px solid var(--border);
    padding: 9px 20px;
    font-size: 14px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-ghost:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }

  .btn-accent {
    background: var(--accent);
    color: #0a0e1a;
    border: none;
    padding: 9px 20px;
    font-size: 14px;
    font-weight: 700;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-accent:hover { background: #33dcff; transform: translateY(-1px); }

  .legal-content {
    max-width: 760px;
    margin: 0 auto;
    padding: 64px 24px 96px;
  }

  .legal-eyebrow {
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.2em;
    color: var(--accent);
    text-transform: uppercase;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .legal-eyebrow::before, .legal-eyebrow::after {
    content: '';
    width: 24px;
    height: 1px;
    background: var(--accent);
    opacity: 0.4;
  }

  .legal-title {
    font-size: 42px;
    font-weight: 800;
    color: var(--text);
    margin-bottom: 12px;
    line-height: 1.1;
  }

  .legal-date {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 48px;
  }

  .legal-intro {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.8;
    margin-bottom: 56px;
    padding: 24px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    border-radius: 0 12px 12px 0;
  }

  .legal-section {
    margin-bottom: 40px;
  }

  .legal-section-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
    font-family: var(--font-mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }

  .legal-section p {
    font-size: 15px;
    color: var(--muted);
    line-height: 1.8;
    margin-bottom: 12px;
  }

  .legal-section p:last-child { margin-bottom: 0; }

  .legal-section ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .legal-section ul li {
    font-size: 15px;
    color: var(--muted);
    line-height: 1.7;
    padding-left: 20px;
    position: relative;
  }

  .legal-section ul li::before {
    content: '—';
    position: absolute;
    left: 0;
    color: var(--accent);
    font-family: var(--font-mono);
  }

  .legal-link { color: var(--accent); text-decoration: none; }
  .legal-link:hover { text-decoration: underline; }

  .footer {
    border-top: 1px solid var(--border);
    padding: 32px 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  .footer-logo { font-size: 16px; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
  .footer-links { display: flex; gap: 24px; align-items: center; }

  .footer-link {
    font-size: 13px;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    transition: color 0.2s;
    text-decoration: none;
  }

  .footer-link:hover { color: var(--text); }
  .footer-copy { font-size: 12px; color: var(--muted); font-family: var(--font-mono); }

  @media (max-width: 600px) {
    .nav { padding: 16px 20px; }
    .legal-content { padding: 40px 20px 80px; }
    .legal-title { font-size: 30px; }
    .footer { padding: 24px 20px; flex-direction: column; align-items: flex-start; }
  }
`;

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <>
      <style>{sharedStyles}</style>
      <div className="page">

        <nav className="nav">
          <div className="nav-logo" onClick={() => navigate("/")}>
            <span>🐧</span>
            PassPenguin
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate("/login")}>Log in</button>
            <button className="btn-accent" onClick={() => navigate("/signup")}>Sign up free</button>
          </div>
        </nav>

        <div className="legal-content">
          <p className="legal-eyebrow">Legal</p>
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-date">Last updated: {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>

          <div className="legal-intro">
            Please read these Terms carefully before using PassPenguin. By creating an account or
            accessing any part of our service, you agree to be bound by these terms.
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">01 · About PassPenguin</h2>
            <p>
              PassPenguin ("we", "our", or "us") is an online study and quiz platform designed to help
              users prepare for Salesforce certification exams. We provide access to practice questions,
              detailed explanations, and progress tracking through a one-time purchase model.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">02 · Eligibility</h2>
            <p>
              There is no lower age limit or other eligibility rules to use PassPenguin.
        
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">03 · Your Account</h2>
            <ul>
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must notify us immediately if you suspect unauthorised access to your account.</li>
              <li>Accounts are personal and may not be shared with or transferred to others.</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">04 · Purchases & Payments</h2>
            <p>
              Access to exam content requires a one-time purchase. All payments are processed securely
              through Stripe. We do not store your payment card details on our servers.
            </p>
            <p>
              Prices are displayed in AUD at checkout. By completing a purchase, you authorise us to
              charge the stated amount to your selected payment method.
            </p>
            <p>
              All sales are final. If you believe you have been charged in error, please contact us at{" "}
              <a href="mailto:hello@passpenguin.com" className="legal-link">hello@passpenguin.com</a>{" "}
              within 14 days of the transaction.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">05 · Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Copy, reproduce, or redistribute any PassPenguin content without our written permission.</li>
              <li>Use the platform for any unlawful purpose or in violation of any applicable regulations.</li>
              <li>Attempt to reverse-engineer, scrape, or extract our question bank or proprietary content.</li>
              <li>Share your account access with others, including for commercial resale.</li>
              <li>Interfere with the security or integrity of the platform.</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">06 · Intellectual Property</h2>
            <p>
              All content on PassPenguin — including practice questions, explanations, branding, and
              software — is owned by or licensed to us and is protected by applicable intellectual
              property laws. Nothing in these Terms grants you ownership of any content.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">07 · Disclaimer of Warranties</h2>
            <p>
              PassPenguin is provided on an "as is" and "as available" basis. While we strive for
              accuracy, we make no warranties that our content will guarantee a passing score on any
              certification exam. Exam formats and syllabi may change without notice.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">08 · Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, PassPenguin shall not be liable for any indirect,
              incidental, or consequential damages arising from your use of — or inability to use — the
              service. Our total liability for any claim shall not exceed the amount you paid us in the
              90 days preceding the claim.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">09 · Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account at any time if you breach these
              Terms. You may delete your account at any time by contacting us. Upon termination, your
              access to purchased content will cease.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">10 · Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will update the date at the
              top of this page and, where appropriate, notify you by email. Continued use of PassPenguin
              after changes are posted constitutes your acceptance of the revised Terms.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">11 · Governing Law</h2>
            <p>
              These Terms are governed by the laws of Queensland, Australia. Any disputes shall be
              subject to the exclusive jurisdiction of the courts of Queensland.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">12 · Contact</h2>
            <p>
              If you have any questions about these Terms, please contact us at{" "}
              <a href="mailto:hello@passpenguin.com" className="legal-link">hello@passpenguin.com</a>.
            </p>
          </div>
        </div>

        <footer className="footer">
          <div className="footer-logo">🐧 PassPenguin</div>
          <div className="footer-links">
            <a className="footer-link" href="/terms-of-service">Terms of Service</a>
            <a className="footer-link" href="/privacy-policy">Privacy Policy</a>
            <a className="footer-link" href="mailto:hello@passpenguin.com">Contact</a>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} PassPenguin</p>
        </footer>

      </div>
    </>
  );
}